import { useAction } from "convex/react";
import { useState } from "react";

import { api } from "@convex/_generated/api";
import {
  billingPortalUnavailable,
  checkoutUnavailable,
} from "@domain/user-errors";
import { logMutationError } from "@/lib/logger";

export type CheckoutKind =
  | "pack-5"
  | "pack-10"
  | "pack-20"
  | "pack-50"
  | "subscription";

export type PendingBillingAction = CheckoutKind | "portal" | null;

/**
 * Hosted-checkout redirect flow: on success the browser navigates away to
 * Stripe, so `pendingAction` intentionally stays set through unload — no
 * polling anywhere; post-payment state arrives via the ledger projection
 * and the lifecycle webhook.
 */
export function useBilling(): {
  startCheckout: (kind: CheckoutKind) => Promise<void>;
  openPortal: () => Promise<void>;
  pendingAction: PendingBillingAction;
  billingError: string | null;
  clearBillingError: () => void;
} {
  const createCheckoutSession = useAction(api.billingActions.createCheckoutSession);
  const createPortalSession = useAction(api.billingActions.createPortalSession);
  const [pendingAction, setPendingAction] = useState<PendingBillingAction>(null);
  const [billingError, setBillingError] = useState<string | null>(null);

  async function startCheckout(kind: CheckoutKind): Promise<void> {
    setBillingError(null);
    setPendingAction(kind);
    try {
      const { url } = await createCheckoutSession({ kind });
      window.location.assign(url);
    } catch (error) {
      logMutationError({
        mutation: "billingActions.createCheckoutSession",
        args: { kind },
        error,
      });
      setBillingError(checkoutUnavailable());
      setPendingAction(null);
    }
  }

  async function openPortal(): Promise<void> {
    setBillingError(null);
    setPendingAction("portal");
    try {
      const { url } = await createPortalSession({});
      window.location.assign(url);
    } catch (error) {
      logMutationError({
        mutation: "billingActions.createPortalSession",
        error,
      });
      setBillingError(billingPortalUnavailable());
      setPendingAction(null);
    }
  }

  return {
    startCheckout,
    openPortal,
    pendingAction,
    billingError,
    clearBillingError: () => setBillingError(null),
  };
}
